// components/landing/sections/FeatureCards.tsx
// ---------------------------------------------------------------------------
// "Why Athena": three glass cards on the Athena signature gradient.
//
// DESIGN (2026-09-09): this section used to sit on a hardcoded pastel gradient
// (#d5b4dd -> #f2dcd0) with three flat pastel blocks, which looked like a
// different product from the rest of Athena. It now uses the same surface as
// the in-app admin forms: the deepNavy -> royalBlue signature gradient, with
// frosted glass cards (alpha(deepNavy, .55) + blur), one accent colour each so
// it still carries colour without the pastel slabs.
//
// Every number below is verified against this repo, not rounded up for effect:
//   44 cancer types  = the cancer.* domain registry, minus the `other` bucket
//   16 tables / 636 fields = the FHIR reshape, cursor_logs + the governance
//   one-pager (presentations/susana_puig/athena_rwe_governance_onepager_v2).
// ---------------------------------------------------------------------------

import { Box, Typography, alpha, useTheme } from "@mui/material";
import {
  Hub as HubIcon,
  VerifiedUser as GovernanceIcon,
  QueryStats as CohortIcon,
} from "@mui/icons-material";
import { SvgIconComponent } from "@mui/icons-material";
import { athenaColors } from "../colors";
import { useTranslation } from "../../../i18n";

interface Feature {
  icon: SvgIconComponent;
  accent: string;
  title: string;
  description: string;
  /** Short verified fact, rendered as a chip. Scannable without reading the body. */
  proof: string;
  /** Card 1 carries the standards logos instead of a plain chip. */
  standards?: boolean;
}

export default function FeatureCards() {
  const theme = useTheme();
  const { t } = useTranslation();

  const FEATURES: Feature[] = [
    {
      icon: HubIcon,
      accent: athenaColors.periwinkle,
      title: t("landing.why_standards_title", "Standards-native, not another silo"),
      description: t(
        "landing.why_standards_desc",
        "Records are structured onto FHIR resources and coded with SNOMED CT as they are captured. Your data leaves as cleanly as it arrives, so it stays usable in registries, data spaces and your own systems.",
      ),
      proof: t("landing.why_standards_proof", "16 FHIR-aligned tables, 636 fields"),
      standards: true,
    },
    {
      icon: GovernanceIcon,
      accent: athenaColors.lightOrange,
      title: t("landing.why_governance_title", "Your data stays governed by you"),
      description: t(
        "landing.why_governance_desc",
        "Each institution keeps its own environment. Consortia share a view of the cohort while every centre controls who may read its records and who may export them. You set the terms with CROs and industry partners.",
      ),
      proof: t("landing.why_governance_proof", "Per-centre read and export control"),
    },
    {
      icon: CohortIcon,
      accent: athenaColors.softPurple,
      title: t("landing.why_cohort_title", "From free text to a queryable cohort"),
      description: t(
        "landing.why_cohort_desc",
        "Reports, notes and scans become structured fields you can question in plain language, then turn into cohorts. Trial matching shows its eligibility reasoning, so a clinician can judge it rather than trust it.",
      ),
      proof: t("landing.why_cohort_proof", "44 cancer types, matched to ClinicalTrials.gov"),
    },
  ];

  return (
    <Box
      id="product"
      sx={{
        py: { xs: 8, md: 12 },
        px: { xs: 3, md: 6 },
        // The Athena signature surface, same family as the in-app glass
        // containers and dialogs. Distinct from the FLAT deepNavy of the
        // "How It Works" section below, so the two do not merge.
        background: `linear-gradient(160deg, ${athenaColors.deepNavy} 0%, ${alpha(
          athenaColors.royalBlue,
          0.92,
        )} 100%)`,
      }}
    >
      <Typography
        variant="h3"
        sx={{
          textAlign: "center",
          fontWeight: 700,
          color: theme.palette.common.white,
          mb: 1.5,
          fontSize: { xs: "1.75rem", md: "2.25rem" },
        }}
      >
        {t("landing.why_athena_title", "Why Athena")}
      </Typography>

      <Typography
        sx={{
          textAlign: "center",
          color: alpha(theme.palette.common.white, 0.7),
          mb: { xs: 5, md: 7 },
          fontSize: { xs: "0.95rem", md: "1.05rem" },
          maxWidth: 720,
          mx: "auto",
          lineHeight: 1.6,
        }}
      >
        {t(
          "landing.why_athena_subtitle",
          "Built on open clinical standards, governed by the institutions that own the data.",
        )}
      </Typography>

      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          gap: { xs: 3, md: 3 },
          maxWidth: 1100,
          mx: "auto",
          alignItems: "stretch",
        }}
      >
        {FEATURES.map((feature) => {
          const Icon = feature.icon;
          return (
            <Box
              key={feature.title}
              sx={{
                flex: 1,
                borderRadius: 3,
                p: { xs: 3, md: 3.5 },
                display: "flex",
                flexDirection: "column",
                position: "relative",
                overflow: "hidden",
                // The in-app glass recipe: translucent navy + blur + hairline.
                background: alpha(athenaColors.deepNavy, 0.55),
                backdropFilter: "blur(20px)",
                WebkitBackdropFilter: "blur(20px)",
                border: `1px solid ${alpha(theme.palette.common.white, 0.22)}`,
                transition: "transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease",
                "&:hover": {
                  transform: "translateY(-6px)",
                  borderColor: alpha(feature.accent, 0.55),
                  boxShadow: `0 16px 48px ${alpha(athenaColors.deepNavy, 0.45)}`,
                },
                // Accent bar: the colour lives here rather than flooding the card.
                "&::before": {
                  content: '""',
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  height: 3,
                  background: feature.accent,
                },
              }}
            >
              <Icon sx={{ fontSize: 40, color: feature.accent, mb: 2 }} />

              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  mb: 1.5,
                  fontSize: { xs: "1.05rem", md: "1.15rem" },
                  lineHeight: 1.3,
                  color: theme.palette.common.white,
                }}
              >
                {feature.title}
              </Typography>

              <Typography
                sx={{
                  fontSize: { xs: "0.88rem", md: "0.92rem" },
                  lineHeight: 1.65,
                  color: alpha(theme.palette.common.white, 0.8),
                  mb: 2.5,
                  flexGrow: 1,
                }}
              >
                {feature.description}
              </Typography>

              {feature.standards ? (
                // Official marks on a light chip: keeps the HL7 FHIR logo in
                // its own colours (trademark use) and guarantees contrast on
                // the dark card.
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 1.5,
                    flexWrap: "wrap",
                    borderRadius: 2,
                    px: 1.5,
                    py: 1.25,
                    // A GLASS chip, not a white slab. At 0.94 white it read as
                    // a foreign element pasted onto the card; the FHIR mark is
                    // red on transparent and carries fine at 0.12 with the
                    // app's usual hairline. Rendered and compared before
                    // choosing: 0.08 loses the panel, 0.16 starts to glare.
                    background: alpha(theme.palette.common.white, 0.12),
                    border: `1px solid ${alpha(theme.palette.common.white, 0.22)}`,
                  }}
                >
                  <Box
                    component="img"
                    src="/images/standards/hl7_fhir.png"
                    alt="HL7 FHIR"
                    sx={{ height: 20, width: "auto", display: "block" }}
                  />
                  <Box
                    sx={{
                      border: `1.5px solid ${alpha(
                        theme.palette.common.white,
                        0.75,
                      )}`,
                      borderRadius: 1,
                      px: 0.9,
                      py: 0.15,
                      fontWeight: 800,
                      fontSize: "0.72rem",
                      letterSpacing: "0.04em",
                      color: theme.palette.common.white,
                      whiteSpace: "nowrap",
                    }}
                  >
                    SNOMED CT
                  </Box>
                </Box>
              ) : (
                <Box
                  sx={{
                    alignSelf: "flex-start",
                    borderRadius: 5,
                    px: 1.5,
                    py: 0.6,
                    fontSize: "0.76rem",
                    fontWeight: 600,
                    lineHeight: 1.3,
                    color: alpha(theme.palette.common.white, 0.92),
                    background: alpha(feature.accent, 0.18),
                    border: `1px solid ${alpha(feature.accent, 0.45)}`,
                  }}
                >
                  {feature.proof}
                </Box>
              )}

              {feature.standards && (
                <Typography
                  sx={{
                    mt: 1.25,
                    fontSize: "0.74rem",
                    fontWeight: 600,
                    color: alpha(theme.palette.common.white, 0.65),
                  }}
                >
                  {feature.proof}
                </Typography>
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
